/**
 * PipelineDashboardTab
 * --------------------------------------------------------------------------
 * Safety-Manager-facing dashboard for the regulatory submission pipeline.
 * Surfaces per-authority KPIs, SLA percentiles, a 30-day time-to-ack
 * histogram, DLQ with one-click replay, and a recent activity feed.
 *
 * This is the KPI surface ICAO USOAP audits inspect when assessing State
 * Safety Programme effectiveness — hence the layout mirrors the Annex 19
 * SPI structure (target / current / trend), not just raw counts.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import {
  AlertTriangle, CheckCircle2, Clock, Inbox, RefreshCw, Play,
  ChevronRight, Activity, TrendingUp,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AUTHORITY_ACCENT = {
  gaca: 'border-l-emerald-500',
  easa: 'border-l-sky-500',
  faa: 'border-l-violet-500',
  icao: 'border-l-amber-500',
};

const STATUS_COLOR = {
  accepted: 'text-emerald-400',
  acknowledged: 'text-emerald-400',
  under_review: 'text-amber-400',
  submitted: 'text-sky-400',
  rejected: 'text-rose-400',
  draft: 'text-slate-400',
  in_dlq: 'text-rose-500',
};

const BUCKET_COLOR = ['#10b981', '#22d3ee', '#facc15', '#f97316', '#ef4444', '#7f1d1d'];

function fmtHours(h) {
  if (h == null) return '—';
  if (h < 1) return `${Math.round(h * 60)}m`;
  if (h < 48) return `${h.toFixed(1)}h`;
  return `${(h / 24).toFixed(1)}d`;
}

export default function PipelineDashboardTab({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [replaying, setReplaying] = useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/regulatory/pipeline-dashboard?days=30`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) {
      toast.error(`Dashboard load failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const replay = async (dlqId) => {
    setReplaying(dlqId);
    try {
      const r = await fetch(`${API_URL}/api/regulatory/dead-letter-queue/${dlqId}/replay`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
      toast.success(`Replayed → ${j.ack?.ack_reference || 'ack received'}`);
      await load();
    } catch (e) {
      toast.error(`Replay failed: ${e.message}`);
    } finally {
      setReplaying(null);
    }
  };

  if (loading && !data) {
    return (
      <div
        className="rounded-lg border border-slate-700 bg-slate-900/40 p-8 text-center text-slate-400 text-sm"
        data-testid="pipeline-dashboard-loading"
      >
        Loading pipeline dashboard…
      </div>
    );
  }
  if (!data) return null;

  const { by_authority, sla, dlq_snapshot, recent_attempts, totals } = data;

  return (
    <div className="space-y-6" data-testid="pipeline-dashboard">
      {/* Top KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard
          label="Total (30d)"
          value={totals.total_submissions}
          icon={<Activity className="w-4 h-4 text-sky-400" />}
          testId="kpi-total"
        />
        <KpiCard
          label="Accepted"
          value={totals.accepted}
          icon={<CheckCircle2 className="w-4 h-4 text-emerald-400" />}
          accent="text-emerald-400"
          testId="kpi-accepted"
        />
        <KpiCard
          label="Under Review"
          value={totals.under_review}
          icon={<Clock className="w-4 h-4 text-amber-400" />}
          accent="text-amber-400"
          testId="kpi-under-review"
        />
        <KpiCard
          label="In DLQ"
          value={totals.in_dlq}
          icon={<AlertTriangle className="w-4 h-4 text-rose-400" />}
          accent="text-rose-400"
          testId="kpi-in-dlq"
        />
      </div>

      {/* Per-authority + SLA */}
      <div className="grid lg:grid-cols-3 gap-3">
        {/* Left col: authority cards, spans 2 */}
        <div className="lg:col-span-2 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-white flex items-center gap-2">
              <Inbox className="w-4 h-4 text-sky-400" />
              Per-Authority Pipeline
            </h3>
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              className="h-7 text-xs gap-1"
              data-testid="pipeline-refresh"
            >
              <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
          {by_authority.length === 0 && (
            <div className="rounded-lg border border-dashed border-slate-700 bg-slate-900/30 p-6 text-center text-slate-500 text-sm">
              No submissions in the last 30 days.
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-3">
            {by_authority.map((a) => (
              <Card
                key={a.authority}
                className={`bg-slate-800/40 border-slate-700 border-l-4 ${AUTHORITY_ACCENT[a.authority] || 'border-l-slate-500'}`}
                data-testid={`authority-card-${a.authority}`}
              >
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-baseline justify-between">
                    <div className="font-bold text-white text-lg uppercase tracking-wider">
                      {a.authority}
                    </div>
                    <div className="text-slate-500 text-xs tabular-nums">{a.total} in 30d</div>
                  </div>
                  <div className="grid grid-cols-3 gap-1 text-xs">
                    <StatusPill label="Accepted" value={a.accepted + a.acknowledged} color={STATUS_COLOR.accepted} />
                    <StatusPill label="Review" value={a.under_review} color={STATUS_COLOR.under_review} />
                    <StatusPill label="Submitted" value={a.submitted} color={STATUS_COLOR.submitted} />
                    <StatusPill label="Rejected" value={a.rejected} color={STATUS_COLOR.rejected} />
                    <StatusPill label="Draft" value={a.draft} color={STATUS_COLOR.draft} />
                    <StatusPill label="In DLQ" value={a.in_dlq} color={STATUS_COLOR.in_dlq} highlight={a.in_dlq > 0} />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Right col: SLA histogram + percentiles */}
        <div>
          <Card className="bg-slate-800/40 border-slate-700" data-testid="sla-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-white flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-violet-400" />
                Time-to-Ack SLA
              </CardTitle>
              <div className="text-[11px] text-slate-500">Occurrence → authority acknowledgment</div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div className="rounded-md bg-slate-900/40 p-2 text-center" data-testid="sla-p50">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Median (p50)</div>
                  <div className="text-lg font-bold text-emerald-300 tabular-nums">{fmtHours(sla.p50_hours)}</div>
                </div>
                <div className="rounded-md bg-slate-900/40 p-2 text-center" data-testid="sla-p90">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Worst 10% (p90)</div>
                  <div className="text-lg font-bold text-amber-300 tabular-nums">{fmtHours(sla.p90_hours)}</div>
                </div>
                <div className="rounded-md bg-slate-900/40 p-2 text-center">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Samples</div>
                  <div className="text-lg font-bold text-sky-300 tabular-nums">{sla.sample_count}</div>
                </div>
              </div>
              <div className="h-[180px]" data-testid="sla-histogram">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={sla.histogram} margin={{ top: 8, right: 5, left: -20, bottom: 0 }}>
                    <CartesianGrid stroke="#334155" strokeDasharray="3 3" opacity={0.4} />
                    <XAxis dataKey="label" tick={{ fill: '#94a3b8', fontSize: 10 }} axisLine={{ stroke: '#334155' }} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} axisLine={{ stroke: '#334155' }} allowDecimals={false} />
                    <Tooltip
                      cursor={{ fill: 'rgba(148,163,184,0.08)' }}
                      contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #334155', fontSize: 12 }}
                    />
                    <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                      {sla.histogram.map((_, i) => (
                        <Cell key={i} fill={BUCKET_COLOR[i] || '#64748b'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* DLQ + Recent activity */}
      <div className="grid lg:grid-cols-2 gap-3">
        <Card className="bg-slate-800/40 border-slate-700" data-testid="dlq-panel">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-white flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-rose-400" />
              Dead-Letter Queue
              {dlq_snapshot.length > 0 && (
                <Badge className="bg-rose-500/15 border border-rose-500/40 text-rose-300 text-[10px] uppercase">
                  {dlq_snapshot.length} awaiting replay
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {dlq_snapshot.length === 0 && (
              <div className="text-xs text-slate-500 py-6 text-center">
                ✓ Nothing parked. All submissions delivered.
              </div>
            )}
            {dlq_snapshot.map((d) => (
              <div
                key={d.id}
                className="rounded-md border border-slate-700 bg-slate-900/30 p-2.5 flex items-center gap-3"
                data-testid={`dlq-row-${d.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-slate-200 truncate">
                    <span className="uppercase text-slate-500 mr-1.5">{d.authority}</span>
                    {d.submission_id}
                  </div>
                  <div className="text-[10px] text-slate-500 truncate">
                    {d.attempts?.length || 0} attempt(s) · parked {(d.parked_at || '').slice(0, 16).replace('T', ' ')}
                  </div>
                  {d.last_error && (
                    <div className="text-[10px] text-rose-400/80 truncate mt-0.5">
                      {d.last_error}
                    </div>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={replaying === d.id}
                  onClick={() => replay(d.id)}
                  className="h-7 text-xs gap-1 border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 flex-shrink-0"
                  data-testid={`dlq-replay-${d.id}`}
                >
                  {replaying === d.id ? (
                    <RefreshCw className="w-3 h-3 animate-spin" />
                  ) : (
                    <Play className="w-3 h-3" />
                  )}
                  Replay
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="bg-slate-800/40 border-slate-700" data-testid="recent-attempts-panel">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-white flex items-center gap-2">
              <Activity className="w-4 h-4 text-sky-400" />
              Recent Attempts
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1.5 max-h-[380px] overflow-y-auto">
            {recent_attempts.length === 0 && (
              <div className="text-xs text-slate-500 py-6 text-center">No recent attempts.</div>
            )}
            {recent_attempts.map((a) => {
              const ok = a.status === 'success';
              return (
                <div
                  key={a.id}
                  className="flex items-center gap-2 text-xs py-1.5 px-2 rounded-md hover:bg-slate-900/40"
                  data-testid={`attempt-row-${a.id}`}
                >
                  <ChevronRight className={`w-3 h-3 flex-shrink-0 ${ok ? 'text-emerald-400' : 'text-rose-400'}`} />
                  <span className="uppercase text-slate-500 w-12 text-[10px]">{a.authority}</span>
                  <span className="tabular-nums text-slate-500 w-8 text-[10px]">#{a.attempt_number}</span>
                  <span className={ok ? 'text-emerald-300' : 'text-rose-300'}>
                    {ok ? '200 OK' : `${a.http_status || 'ERR'}`}
                  </span>
                  <span className="text-slate-500 text-[10px] ml-auto flex-shrink-0">
                    {(a.started_at || '').slice(11, 19)}
                  </span>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function KpiCard({ label, value, icon, accent = 'text-white', testId }) {
  return (
    <Card className="bg-slate-800/40 border-slate-700" data-testid={testId}>
      <CardContent className="p-3">
        <div className="flex items-center justify-between mb-1">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
          {icon}
        </div>
        <div className={`text-2xl font-bold tabular-nums ${accent}`}>{value}</div>
      </CardContent>
    </Card>
  );
}

function StatusPill({ label, value, color, highlight = false }) {
  return (
    <div
      className={`rounded px-1.5 py-1 text-center ${
        highlight ? 'bg-rose-500/10 border border-rose-500/30' : 'bg-slate-900/30'
      }`}
    >
      <div className={`font-bold tabular-nums ${color}`}>{value}</div>
      <div className="text-[9px] uppercase tracking-wider text-slate-500">{label}</div>
    </div>
  );
}
